import {
  ROUTES_AT_RISK,
  DONE,
  NOT_STARTED,
  PROGRESSING,
  STOW_PENDING,
  STAGE_PENDING,
  STAGE_IN_PROGRESS,
  STAGED,
  STOWED,
  PICK_PENDING,
  PICK_IN_PROGRESS,
  STATUS_TO_METRIC_KEY_MAP,
} from "./EntityStatusManager";
import {
  getRouteStatusMetrics,
  hasCoreWorkGroupSeq,
  hasGroceryWorkGroupSeq,
} from "./Util";

/**
 * Enum for product types representing different route categories
 */
export const PRODUCT_TYPES = {
  ALL_ROUTES: "All routes",
  CORE_PICKLIST: "Core picklist",
  GROCERY_PICKLIST: "Grocery picklist",
};

/**
 * Status configurations for core picklist routes
 */
export const CORE_PICKLIST_STATUS = {
  ROUTES_AT_RISK: ROUTES_AT_RISK,
  STOW_PENDING: STOW_PENDING,
  STOWED: STOWED,
  PICK_PENDING: PICK_PENDING,
  PICK_IN_PROGRESS: PICK_IN_PROGRESS,
  STAGE_PENDING: STAGE_PENDING,
  STAGE_IN_PROGRESS: STAGE_IN_PROGRESS,
  STAGED: STAGED,
};

/**
 * Status configurations for grocery picklist routes
 */
export const GROCERY_PICKLIST_STATUS = {
  ROUTES_AT_RISK: ROUTES_AT_RISK,
  STOW_PENDING: STOW_PENDING,
  STOWED: STOWED,
  PICK_PENDING: PICK_PENDING,
  PICK_IN_PROGRESS: PICK_IN_PROGRESS,
  STAGE_IN_PROGRESS: STAGE_IN_PROGRESS,
  STAGED: STAGED,
};

/**
 * Status configurations for all routes
 */
export const ALL_ROUTES_STATUS = {
  ROUTES_AT_RISK: ROUTES_AT_RISK,
  DONE: DONE,
  NOT_STARTED: NOT_STARTED,
  PROGRESSING: PROGRESSING,
};

/**
 * Maps product types to their corresponding status configurations
 */
export const PRODUCT_TYPE_TO_ROUTE_STATUS = {
  [PRODUCT_TYPES.ALL_ROUTES]: ALL_ROUTES_STATUS,
  [PRODUCT_TYPES.CORE_PICKLIST]: CORE_PICKLIST_STATUS,
  [PRODUCT_TYPES.GROCERY_PICKLIST]: GROCERY_PICKLIST_STATUS,
};

/**
 * Processes route status metrics for a given route and product type
 * @param {Object} route - Route object to process
 * @param {string} productType - Type of product
 * @param {Function} callback - Callback function to execute for each matching status
 */
export function processRouteStatusMetrics(route, productType, callback) {
  const statusConfig = PRODUCT_TYPE_TO_ROUTE_STATUS[productType];
  const routeStatusMetrics = getRouteStatusMetrics(route, productType);

  Object.values(statusConfig).forEach((status) => {
    const metricKey = STATUS_TO_METRIC_KEY_MAP[status];
    if (metricKey && routeStatusMetrics[metricKey]) {
      callback(metricKey, status);
    }
  });
}

/**
 * Calculates metrics for routes based on their status
 * @param {Array} routes - Array of route objects to calculate metrics for
 * @param {string} productType - Type of product to filter routes by
 * @returns {Object} Object containing counts of routes in different stages
 */
export function getRouteStatusMetricCount(routes, productType) {
  const metrics = {
    isAtRisk: 0,
    stowPending: 0,
    stowed: 0,
    pickPending: 0,
    pickInProgress: 0,
    stagePending: 0,
    stageInProgress: 0,
    staged: 0,
    notStarted: 0,
    progressing: 0,
    done: 0,
  };

  routes.forEach((route) => {
    processRouteStatusMetrics(route, productType, (metricKey) => {
      metrics[metricKey]++;
    });
  });

  return metrics;
}

/**
 * Configuration object defining handlers and allowed statuses for each product type
 */
export const PRODUCT_TYPE_HANDLERS = {
  [PRODUCT_TYPES.ALL_ROUTES]: {
    typeChecker: null,
    allowedStatuses: Object.values(ALL_ROUTES_STATUS),
  },
  [PRODUCT_TYPES.CORE_PICKLIST]: {
    typeChecker: hasCoreWorkGroupSeq,
    allowedStatuses: Object.values(CORE_PICKLIST_STATUS),
  },
  [PRODUCT_TYPES.GROCERY_PICKLIST]: {
    typeChecker: hasGroceryWorkGroupSeq,
    allowedStatuses: Object.values(GROCERY_PICKLIST_STATUS),
  },
};

export const getRouteStatusList = (productType, dropToCartEnabled = false) => {
  const statusConfig =
    PRODUCT_TYPE_TO_ROUTE_STATUS[productType] || ALL_ROUTES_STATUS;
  let statusList = Object.values(statusConfig).filter(Boolean);

  if (productType === PRODUCT_TYPES.CORE_PICKLIST && !dropToCartEnabled) {
    statusList = statusList.filter(
      (status) => status !== PICK_PENDING && status !== PICK_IN_PROGRESS,
    );
  }

  return statusList;
};



// WEBPACK FOOTER //
// ./src/components/RouteStatus/utils/ProductTypeUtils.js